import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface GridItemWrapperProps {
  title: string;
  isEditable: boolean;
  isInput?: boolean;
  isSummary?: boolean;
  isShared?: boolean;
  hideMaximize?: boolean;
  onRefresh?: () => void;
  children: React.ReactNode;
}

const GridItemWrapper: React.FC<GridItemWrapperProps> = ({
  title,
  isEditable,
  isInput,
  isSummary,
  isShared,
  hideMaximize,
  onRefresh,
  children,
}) => {
  const [maximized, setMaximized] = useState(false);
  const [spinning, setSpinning] = useState(false);

  const handleMaximize = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setMaximized(true);
  }, []);

  const handleMinimize = useCallback(() => {
    setMaximized(false);
  }, []);

  const handleRefresh = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRefresh) {
      setSpinning(true);
      onRefresh();
      setTimeout(() => setSpinning(false), 800);
    }
  }, [onRefresh]);

  // ESC to close
  useEffect(() => {
    if (!maximized) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMaximized(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [maximized]);

  return (
    <>
      <div className="grid-item-container h-full flex flex-col">
        {/* Title bar */}
        <div
          className={`grid-item-titlebar ${isEditable ? 'grid-item-titlebar--editable' : ''} ${isInput ? 'grid-item-titlebar--input' : ''}`}
        >

          <span className="flex-1 truncate text-xs font-medium text-neutral-700">
            {title}
          </span>
          {!isEditable && onRefresh && (
            <button
              onClick={handleRefresh}
              className="p-0.5 rounded hover:bg-neutral-200 text-neutral-400 hover:text-neutral-600 transition-colors"
              title="Refresh data"
            >
              <svg className={`w-3.5 h-3.5 ${spinning ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}
          {!isEditable && !hideMaximize && (
            <button
              onClick={handleMaximize}
              className="p-0.5 rounded hover:bg-neutral-200 text-neutral-400 hover:text-neutral-600 transition-colors"
              title="Maximize"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
              </svg>
            </button>
          )}
        </div>
        {/* Widget content */}
        <div className="grid-item-content flex-1 overflow-auto min-h-0">
          {children}
        </div>
      </div>

      {/* Maximized fullscreen overlay */}
      {maximized && createPortal(
        <div className="fixed inset-0 z-50 bg-surface flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-neutral-200 bg-neutral-50 flex-shrink-0">

            <span className="flex-1 font-medium text-neutral-800">{title}</span>
            {onRefresh && (
              <button
                onClick={handleRefresh}
                className="p-1.5 rounded-lg hover:bg-neutral-200 text-neutral-500 hover:text-neutral-700 transition-colors"
                title="Refresh data"
              >
                <svg className={`w-4 h-4 ${spinning ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            )}
            <button
              onClick={handleMinimize}
              className="p-1.5 rounded-lg hover:bg-neutral-200 text-neutral-500 hover:text-neutral-700 transition-colors"
              title="Close (Esc)"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Content */}
          <div className="flex-1 overflow-auto min-h-0 p-4">
            {children}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default React.memo(GridItemWrapper);
